import React, { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import SetupPage from './SetupPage';
import { getEntitlementsSection, canAccessSetup, hasPackage } from '../../utils/entitlements';
import { resolveProductBuilderMode } from '../../utils/productBuilderMode';
import { canShowAiChatbotIndexingInSetup } from '../../utils/aiChatbotIndexingMode';
import { canShowVisitMobileConfigInSetup } from '../../utils/visitMobileConfigSetup';
import authApi from '../../api/authApi';

const SETUP_PATHS = [
  { id: 'objectmanager', path: '/setup/objectmanager' },
  { id: 'layout-recordview-roles', path: '/setup/layout-recordview-roles' },
  { id: 'rolemanager', path: '/setup/rolemanager' },
  { id: 'tabs', path: '/setup/tabs' },
  { id: 'appmanager', path: '/setup/appmanager' },
  { id: 'actions', path: '/setup/actions' },
  { id: 'custom-scripts', path: '/setup/custom-scripts' },
  { id: 'workflows', path: '/setup/workflows' },
  { id: 'homewidget', path: '/setup/homewidget' },
  { id: 'webforms', path: '/setup/webforms' },
  { id: 'scoringrule', path: '/setup/scoringrule' },
  { id: 'object-health', path: '/setup/object-health' },
  { id: 'audit-center', path: '/setup/audit-center' },
  { id: 'storage-usage', path: '/setup/storage-usage' },
  { id: 'usergroups', path: '/setup/usergroups' },
  { id: 'users', path: '/setup/users' },
  { id: 'mailconfig', path: '/setup/mailconfig' },
  { id: 'emailtemplate', path: '/setup/emailtemplate' },
  { id: 'emaillayout', path: '/setup/emaillayout' },
  { id: 'assignmentrule', path: '/setup/assignmentrule' },
  { id: 'sharingrules', path: '/setup/sharingrules' },
  { id: 'searchviews', path: '/setup/searchviews' },
  { id: 'session_setting', path: '/setup/session_setting' },
  { id: 'scheduled-jobs', path: '/setup/scheduled-jobs' },
  { id: 'currency', path: '/setup/currency/master' },
  { id: 'system_settings', path: '/setup/system_settings' },
  { id: 'stage_master', path: '/setup/stage_master' },
  { id: 'telecmi', path: '/setup/telecmi' },
  { id: 'mis-settings', path: '/setup/mis-settings' },
  { id: 'visit-mobile-config', path: '/setup/visit-mobile-config' },
  { id: 'aichatbot', path: '/setup/aichatbot' },
];

const getFirstAllowedSetupPath = (setupConfig, excludeIds = []) => {
  const allowedItems = new Set(
    (setupConfig?.allowedItems || []).map(item => String(item).toLowerCase())
  );
  const excludeSet = new Set(excludeIds.map(id => String(id).toLowerCase()));
  const isAllowlist = setupConfig?.mode === 'allowlist';
  const isDenylist = setupConfig?.mode === 'denylist';

  const paths = SETUP_PATHS.filter(item => !excludeSet.has(item.id.toLowerCase()));

  if (isAllowlist) {
    const match = paths.find(item => allowedItems.has(item.id));
    return match?.path || null;
  }

  if (isDenylist) {
    const match = paths.find(item => !allowedItems.has(item.id));
    return match?.path || null;
  }

  return paths[0]?.path || null;
};

const resolveSetupAccessId = currentId => {
  if (!currentId) return currentId;
  if (currentId === 'permission') return 'rolemanager';
  /** Same access as Object Manager (layouts / record views). Not always in allowlist as its own id. */
  if (currentId === 'layout-recordview-roles') return 'objectmanager';
  return currentId;
};

const SetupWrapper = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const run = async () => {
      try {
        // Use entitlements for setup allowlist/denylist; `canAccessSetup()` is ADMIN license on JWT (not setup.showSetup).
        let setupConfig = getEntitlementsSection('setup', {});
        if (!setupConfig || Object.keys(setupConfig).length === 0) {
          await authApi.fetchEntitlements();
          setupConfig = getEntitlementsSection('setup', {});
        }
        const hasSetupPermission = canAccessSetup();

        if (!hasSetupPermission) {
          navigate('/access-denied', { replace: true });
          return;
        }

        const workflowEnt = getEntitlementsSection('workflow', {});
        const allowedSetupItems = new Set(
          (setupConfig?.allowedItems || []).map(item =>
            String(item).toLowerCase()
          )
        );
        const isAllowlist = setupConfig?.mode === 'allowlist';
        const isDenylist = setupConfig?.mode === 'denylist';
        const workflowsInList = allowedSetupItems.has('workflows');
        const excludeWorkflows =
          workflowEnt.allowViewWorkflow === false ||
          (isAllowlist ? !workflowsInList : isDenylist ? workflowsInList : !workflowsInList);

        const path = window.location.pathname || '';
        const match = path.match(/\/setup\/([^/]+)/);
        const currentId = resolveSetupAccessId(match?.[1]?.toLowerCase());
        if (currentId) {
          let isAllowed = isAllowlist
            ? allowedSetupItems.has(currentId)
            : isDenylist
              ? !allowedSetupItems.has(currentId)
              : true;
          if (currentId === 'workflows' && excludeWorkflows) isAllowed = false;
          if (currentId === 'custom-scripts') {
            if (isAllowlist) {
              isAllowed = allowedSetupItems.has('custom-scripts');
            } else if (isDenylist) {
              isAllowed = !allowedSetupItems.has('custom-scripts');
            }
          }
          if (currentId === 'currency') {
            if (path.includes('/setup/currency/master')) isAllowed = true;
            else if (path.includes('/setup/currency/exchange-rate')) isAllowed = isAllowlist ? allowedSetupItems.has('currency') : isDenylist ? !allowedSetupItems.has('currency') : true;
          }
          if (currentId === 'telecmi' && hasPackage('telecmi')) {
            isAllowed = true;
          }
          if (currentId === 'mis-settings' && hasPackage('mis')) {
            isAllowed = true;
          }
          if (currentId === 'aichatbot') {
            const productBuilder = await resolveProductBuilderMode();
            if (canShowAiChatbotIndexingInSetup(productBuilder)) {
              isAllowed = true;
            } else {
              isAllowed = false;
            }
          }
          if (currentId === 'object-health') {
            const productBuilder = await resolveProductBuilderMode();
            if (productBuilder) {
              isAllowed = true;
            }
          }
          if (currentId === 'visit-mobile-config') {
            const productBuilder = await resolveProductBuilderMode();
            if (
              hasPackage('visit_manager') &&
              canShowVisitMobileConfigInSetup(
                productBuilder,
                allowedSetupItems
              )
            ) {
              isAllowed = true;
            } else {
              isAllowed = false;
            }
          }
          if (currentId === 'audit-center') {
            isAllowed = isDenylist ? !allowedSetupItems.has('audit-center') : true;
          }
          if (currentId === 'storage-usage') {
            isAllowed = isDenylist ? !allowedSetupItems.has('storage-usage') : true;
          }
          if (currentId === 'sharingrules') {
            isAllowed = isAllowlist
              ? allowedSetupItems.has('sharingrules') ||
              allowedSetupItems.has('assignmentrule')
              : isDenylist
                ? !allowedSetupItems.has('sharingrules') &&
                !allowedSetupItems.has('assignmentrule')
                : true;
          }
          if (!isAllowed) {
            const fallback = getFirstAllowedSetupPath(setupConfig, excludeWorkflows ? ['workflows'] : []);
            navigate(fallback || '/access-denied', { replace: true });
          }
        } else {
          const fallback = getFirstAllowedSetupPath(setupConfig, excludeWorkflows ? ['workflows'] : []);
          if (fallback && path === '/setup') {
            navigate(fallback, { replace: true });
          }
        }
      } catch (error) {
        console.error('Setup access check error:', error);
        navigate('/login');
      }
    };
    run();
  }, [navigate]);

  return (
    <SetupPage>
      <Outlet />
    </SetupPage>
  );
};

export default SetupWrapper;
